import type { Board } from "../board";
import type { DistanceMap } from "../distanceMap";
import { directionDelta, directionFromVector, cycleDirection } from "../direction";
import type { ControllableByAI } from "../interfaces";
import { Mob } from "../mob";
import type { PhysicalThingWithHp } from "../physicalThingWithHp";
import { randomChance, randomElement, randomInt } from "../random";
import { SolidifiedMob } from "../things/solidifiedMob";
import { Rock } from "../things/rock";
import { CaveRock } from "../things/caveRock";
import { Portal2Part } from "../things/portal2/portal2Part";
import type { Sound } from "../things/sounds/sound";
import { XY } from "../xy";
import { updateLookDirectionFromHorizontalDelta } from "./movement";

export class BasicAI {
	protected actor: ControllableByAI;
	probabilityToBreakBlockWhenStuck: number = 0;
	probabilityToMoveWhenIdle: number = 0.3;
	alwaysKnowsWhereTargetIs: boolean = false;
	aggroDistanceReachable: number = 45;
	aggroDistanceRadius: number = 7;
	probabilityToAttack: number = 0.9;
	probabilityToActWhenNotIdle: number = 0.9;

	lastNoiseTurn: number | null = null;
	noiseCoolDown: number = 10;
	minimumDistanceToNoise: number = 10;
	noiseProbability: number = 0.05;

	noiseSoundSupplier: (() => Sound) | null = null;
	attackSoundSupplier: (() => Sound) | null = null;
	movementSoundSupplier: (() => Sound) | null = null;

	constructor(actor: ControllableByAI) {
		this.actor = actor;
	}

	protected getBoard(): Board {
		return this.actor.getBoard();
	}

	processTurn(): void {
		const previousXY = this.actor.getXY();

		const target = this.getTarget();
		if (!target || !target.active) {
			this.idle();
		} else if (this.actor.canAttack(target)) {
			if (randomChance(this.probabilityToAttack)) this.attack(target);
		} else if (!this.knowsWhereTargetIs(target)) {
			this.idle();
		} else if (randomChance(this.probabilityToActWhenNotIdle)) {
			if (!this.moveTowardsTarget(target)) {
				if (!this.breakSomethingTowardsTarget(target)) {
					this.moveTowardsTargetHeuristically(target);
				}
			}
		}

		updateLookDirectionFromHorizontalDelta(this.actor, previousXY, this.actor.getXY());

		this.processSounds();
	}

	protected getTarget(): PhysicalThingWithHp | null {
		return this.getBoard().player;
	}

	protected knowsWhereTargetIs(target: PhysicalThingWithHp): boolean {
		if (this.alwaysKnowsWhereTargetIs) return true;
		if (target === this.getBoard().player) {
			const distanceMap = this.getDistanceMapToPlayer();
			if (distanceMap) {
				const distance = distanceMap.get(this.actor.getXY());
				if (distance !== undefined && distance <= this.getEffectiveAggroReachable()) return true;
			}
		}
		return target.xy.distanceTo(this.actor.getXY()) <= this.getEffectiveAggroRadius();
	}

	// Effective aggro is read through these so subclasses can scale it from live state
	protected getEffectiveAggroReachable(): number {
		return this.aggroDistanceReachable;
	}

	protected getEffectiveAggroRadius(): number {
		return this.aggroDistanceRadius;
	}

	protected getDistanceMapToPlayer(): DistanceMap | null {
		return this.getBoard().getDistanceMapToPlayer();
	}

	protected moveTowardsTarget(target: PhysicalThingWithHp): boolean {
		const targetXY = target.xy;
		if (targetXY.equals(this.actor.getXY())) return false;

		const deltax = targetXY.x - this.actor.getXY().x;
		const deltay = targetXY.y - this.actor.getXY().y;
		const vector = new XY(Math.sign(deltax), Math.sign(deltay));

		let direction = directionFromVector(vector);
		if (!direction) return false;
		direction = cycleDirection(direction, randomInt(-1, 1));

		const proposedXY = this.actor.getXY().add(directionDelta(direction));
		if (!this.actor.canMoveTo(proposedXY)) return false;

		this.moveActorTo(proposedXY);
		return true;
	}

	protected breakSomethingTowardsTarget(target: PhysicalThingWithHp): boolean {
		if (!randomChance(this.probabilityToBreakBlockWhenStuck)) return false;

		const targetXY = target.xy;
		const deltax = targetXY.x - this.actor.getXY().x;
		const deltay = targetXY.y - this.actor.getXY().y;
		const vector = new XY(Math.sign(deltax), Math.sign(deltay));

		let direction = directionFromVector(vector);
		if (!direction) return false;
		direction = cycleDirection(direction, randomInt(-1, 1));

		const proposedXY = this.actor.getXY().add(directionDelta(direction));
		const board = this.getBoard();
		if (!board.isInBounds(proposedXY) || board.hasMob(proposedXY)) return false;

		if (!board.isPassable(proposedXY)) {
			const things = board.getThingsAt(proposedXY);
			for (const thing of things) {
				if (!this.isDamageableObstacle(thing as PhysicalThingWithHp)) continue;
				if (!this.actor.canAttack(thing as PhysicalThingWithHp)) continue;
				this.attack(thing as PhysicalThingWithHp);
				return true;
			}
		}

		return false;
	}

	protected isDamageableObstacle(thing: PhysicalThingWithHp): boolean {
		return (
			thing instanceof SolidifiedMob ||
			thing instanceof Rock ||
			thing instanceof CaveRock ||
			(thing instanceof Mob && thing.isFrozen())
		);
	}

	protected moveTowardsTargetHeuristically(target: PhysicalThingWithHp): boolean {
		const neighbors = this.getBoard().getNeighbors(this.actor.getXY());
		const passable = neighbors.filter((n) => this.actor.canMoveTo(n));

		let best: XY | null = null;
		const targetXY = target.xy;
		for (const n of passable) {
			if (!best || n.distanceTo(targetXY) <= best.distanceTo(targetXY)) {
				best = n;
			}
		}

		if (!best) return false;
		this.moveActorTo(best);
		return true;
	}

	/** True if the actor is on a tile where it shouldn't stay (e.g. on top of a portal or crud). */
	protected isStuck(): boolean {
		const xy = this.actor.getXY();
		const things = this.getBoard().getThingsAt(xy);
		return things.some((t) => t instanceof Portal2Part || t instanceof Rock || t instanceof CaveRock);
	}

	protected idle(): void {
		if (this.isStuck() || randomChance(this.probabilityToMoveWhenIdle)) {
			const neighbors = this.getBoard().getNeighbors(this.actor.getXY());
			const passable = neighbors.filter((n) => this.actor.canMoveTo(n));
			const destination = randomElement(passable);
			if (destination) this.moveActorTo(destination);
		}
	}

	// Last line of defence: Mob.moveTo does no validation of its own, so a planner bug
	// would otherwise show up as an actor walking through an obstacle.
	protected moveActorTo(xy: XY): void {
		if (!this.actor.canMoveTo(xy)) return;
		this.actor.moveTo(xy);
		this.addSound(this.movementSoundSupplier);
	}

	protected attack(target: PhysicalThingWithHp): void {
		this.actor.attack(target);
		this.addSound(this.attackSoundSupplier);
	}

	protected addSound(supplier: (() => Sound) | null): void {
		if (supplier === null) return;
		this.getBoard().addNonPhysicalThing(supplier());
	}

	protected processSounds(): void {
		if (this.noiseSoundSupplier === null) return;
		const target = this.getBoard().player;
		if (!target || !target.active) return;
		const distance = this.actor.getXY().distanceTo(target.xy);
		if (
			distance <= this.minimumDistanceToNoise &&
			randomChance(this.noiseProbability) &&
			(this.lastNoiseTurn === null || this.getBoard().turnCount - this.lastNoiseTurn > this.noiseCoolDown)
		) {
			this.addSound(this.noiseSoundSupplier);
			this.lastNoiseTurn = this.getBoard().turnCount;
		}
	}

	// Runtime state that must survive save/resume. see docs/serialization.md "AI State".
	serialize(): Record<string, unknown> {
		return { lastNoiseTurn: this.lastNoiseTurn };
	}

	restoreFrom(data: any): void {
		if (!data) return;
		this.lastNoiseTurn = data.lastNoiseTurn ?? null;
	}
}
